import { createMedia } from '@artsy/fresnel';
import { DomainList } from 'common/constants/domain';
import { DomainContext } from 'common/layouts/LayoutContext';
import { getCurrentOrganization } from 'common/selectors/auth';
import ErrorWrapper from 'error-handling/index';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useGetOrganizationEmployeesQuery } from '../../../features/employee/employeeApi';
import MobileNav from './MobileNav';
import SideBar from './SideBar';
import TopBar from './TopBar';
import CopilotSlidePanel from '@/areas/copilot/components/CopilotSlidePanel';
import { CopilotPagePromptsProvider } from '@/areas/copilot/components/CopilotPromptsContext';
import { MobileNavButtonDescriptor, TopBarContext } from './TopBarContext';
import { SidebarProvider, useSidebar } from '../../contexts/SidebarContext';
import styled from 'styled-components';

const { MediaContextProvider, Media } = createMedia({
  breakpoints: {
    mobile: 0,
    tablet: 768,
    computer: 1024,
  },
});

interface ComplianceCheckProps {
  children: ReactNode;
}

interface UserContext {
  user: {
    account_id: number;
  };
}

interface Organization {
  id: number;
}

interface Domain {
  id: number;
  name: string;
  url: string;
  image: string;
  fullImage: string;
  loginText: string;
  allowRegister: boolean;
  registerText: string;
  notRegisterText: string;
  showGoogleSso: boolean;
  debug: boolean;
  homePage: string;
  privacyPolicy: string;
  googleClientId: string;
  googleScope: string;
}

const ComplianceCheck: React.FC<ComplianceCheckProps> = props => {
  let navigate = useNavigate();
  let location = useLocation();
  const currentOrganization = useSelector(getCurrentOrganization) as Organization;
  const { user } = useSelector((state: { userContext: UserContext }) => state.userContext);

  const { data: employeeData } = useGetOrganizationEmployeesQuery(
    {
      id: 0, // Required by generated API but not used for list operations
      organizationId: currentOrganization?.id || 0,
      accountId: user?.account_id || 0,
    },
    {
      skip: !currentOrganization?.id || !user?.account_id,
    }
  );

  // Check for 401 errors in the employee query
  if (location.pathname !== '/account/login' && employeeData?.error?.status === 401) {
    navigate('/account/login');
    return null;
  }

  return props.children;
};

const SIDEBAR_EXPANDED_SLIDE_IN_BREAKPOINT = 1920;
const SIDEBAR_COLLAPSED_SLIDE_IN_BREAKPOINT = SIDEBAR_EXPANDED_SLIDE_IN_BREAKPOINT - 230;

const LayoutContainer: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [help, setHelp] = useState(false);
  const [topBarContent, setTopBarContent] = useState<ReactNode | null>(null);
  const [navButton, setNavButton] = useState<React.ReactNode | MobileNavButtonDescriptor | null>(null);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(0);
  const { isOpen } = useSidebar();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const updateViewportWidth = () => setViewportWidth(window.innerWidth || 0);
      updateViewportWidth();
      window.addEventListener('resize', updateViewportWidth);
      return () => window.removeEventListener('resize', updateViewportWidth);
    }
    return undefined;
  }, []);

  return (
    <>
      {/* Desktop View */}
      <div>
        <SideBar />
        <TopBarContext.Provider
          value={{
            setTopBarContent,
            setNavButton,
            openCopilot: () => setCopilotOpen(true),
            topBarContent,
            navButton,
          }}
        >
          <TopBar
            setHelp={() => setHelp(!help)}
            topBarContent={topBarContent}
            copilotOpen={copilotOpen}
            onCopilotToggle={() => setCopilotOpen(prev => !prev)}
          />

          <PageContentWrapper
            id="page-content-wrapper"
            $isOpen={isOpen}
            $isCopilotOpen={copilotOpen}
            $canSlideIn={viewportWidth >= (isOpen ? SIDEBAR_EXPANDED_SLIDE_IN_BREAKPOINT : SIDEBAR_COLLAPSED_SLIDE_IN_BREAKPOINT)}
          >
            <ContentContainer>
              <ErrorWrapper>
                <Outlet />
              </ErrorWrapper>
            </ContentContainer>
          </PageContentWrapper>

          <CopilotSlidePanel isOpen={copilotOpen} onClose={() => setCopilotOpen(false)} />
        </TopBarContext.Provider>
      </div>
    </>
  );
};

interface PageContentWrapperProps {
  $isOpen: boolean;
  $isCopilotOpen: boolean;
  $canSlideIn: boolean;
}

const PageContentWrapper = styled.div<PageContentWrapperProps>`
  position: fixed;
  top: 50px;
  left: ${props => (props.$isOpen ? '250px' : '50px')};
  right: ${props => (props.$isCopilotOpen && props.$canSlideIn ? '420px' : '0')};
  bottom: 0;
  width: ${props => {
    if (props.$isCopilotOpen && props.$canSlideIn) {
      return props.$isOpen ? 'calc(100vw - 250px - 420px)' : 'calc(100vw - 50px - 420px)';
    }
    return props.$isOpen ? 'calc(100vw - 250px)' : 'calc(100vw - 50px)';
  }};
  overflow: hidden;
  transition:
    left 0.3s ease-in-out,
    width 0.3s ease-in-out,
    right 0.3s ease-in-out;
  background-color: #fff;
  z-index: 1;
`;

const ContentContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 20px;
  box-sizing: border-box;
  
  /* Remove padding for components that opt out */
  & > [data-no-container-padding="true"] {
    margin: -20px;
    width: calc(100% + 40px);
    height: calc(100% + 40px);
  }
`;

interface ResponsiveContainerProps {
  children: ReactNode;
}

const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({ children }) => {
  const domain = window.location.host;
  const defaultDomain = Object.values(DomainList)[0] as Domain;
  const currentDomain = (DomainList as Record<string, Domain>)[domain] || defaultDomain;

  return (
    <MediaContextProvider>
      <DomainContext.Provider value={currentDomain}>
        <SidebarProvider>
          <CopilotPagePromptsProvider>
            <ComplianceCheck>
              <LayoutContainer>{children}</LayoutContainer>
            </ComplianceCheck>
          </CopilotPagePromptsProvider>
        </SidebarProvider>
      </DomainContext.Provider>
    </MediaContextProvider>
  );
};

export default ResponsiveContainer;
